import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import SafeIcon from '../common/SafeIcon';
import pollService from '../services/pollService';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiPause, FiDownload, FiCopy, FiUsers, FiMessageSquare, FiTrendingUp, FiShare2 } = FiIcons;

const HostDashboard = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [poll, setPoll] = useState(null);
  const [isRoundActive, setIsRoundActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);
  const [error, setError] = useState(null);

  const hostToken = searchParams.get('token');
  const participantUrl = `${window.location.origin}/#/poll/${id}`;

  useEffect(() => {
    if (!hostToken) {
      navigate('/');
      return;
    }

    fetchPollData();

    // Set up polling for real-time updates
    const interval = setInterval(fetchPollData, 2000);
    return () => clearInterval(interval);
  }, [id, hostToken]);

  const fetchPollData = async () => {
    try {
      const data = await pollService.getPoll(id, hostToken);
      setPoll(data);
      setIsRoundActive(data.currentRound?.active || false);
      setError(null);
    } catch (error) {
      console.error('Error fetching poll:', error);
      setError(error.message);
      if (error.message === 'Poll not found') {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleRound = async () => {
    try {
      await pollService.manageRound(id, hostToken, isRoundActive ? 'stop' : 'start');
      setIsRoundActive(!isRoundActive);
      fetchPollData(); // Refresh data
    } catch (error) {
      console.error('Error toggling round:', error);
      setError(error.message);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(participantUrl);
    alert('Link kopiert!');
  };

  const downloadData = async (format) => {
    try {
      const csvData = await pollService.exportPoll(id, hostToken, format);
      if (csvData) {
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `umfrage-${id}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error downloading data:', error);
      setError('Fehler beim Herunterladen der Daten');
    }
  };

  const getCategorySize = (category) => {
    if (!poll) return 'col-span-1 row-span-1';
    
    const totalInputs = poll.categories.reduce((sum, cat) => sum + (cat.totalInputs || 0), 0);
    if (totalInputs === 0) return 'col-span-1 row-span-1';
    
    const ratio = (category.totalInputs || 0) / totalInputs;
    
    if (ratio > 0.4) return `col-span-2 row-span-2`;
    if (ratio > 0.2) return `col-span-2 row-span-1`;
    return 'col-span-1 row-span-1';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary mb-4">Umfrage nicht gefunden</h2>
          {error && <p className="text-red-600 mb-4">{error}</p>}
          <button
            onClick={() => navigate('/')}
            className="btn-primary px-6 py-2 rounded-lg"
          >
            Zur Startseite
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-primary">{poll.question}</h1>
            <p className="text-primary">Host-Dashboard</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={toggleRound}
              className="btn-primary flex items-center px-4 py-2 rounded-lg font-medium"
            >
              <SafeIcon icon={isRoundActive ? FiPause : FiPlay} className="mr-2" />
              {isRoundActive ? 'Runde stoppen' : 'Runde starten'}
            </button>
            
            <button
              onClick={() => setShowQRCode(!showQRCode)}
              className="btn-primary flex items-center px-4 py-2 rounded-lg"
            >
              <SafeIcon icon={FiShare2} className="mr-2" />
              Teilen
            </button>
            
            <button
              onClick={() => downloadData('csv')}
              className="btn-primary flex items-center px-3 py-2 rounded-lg"
            >
              <SafeIcon icon={FiDownload} className="mr-2" />
              CSV
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Share Section */}
        {showQRCode && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-50 border rounded-lg mb-6"
          >
            <div className="p-6">
              <div className="flex flex-col lg:flex-row items-center justify-center space-y-4 lg:space-y-0 lg:space-x-8">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-primary mb-2">Teilnehmer-Link</h3>
                  <div className="flex items-center space-x-2 bg-white rounded-lg px-4 py-2 border">
                    <input
                      type="text"
                      value={participantUrl}
                      readOnly
                      className="flex-1 bg-transparent text-primary text-sm"
                    />
                    <button
                      onClick={copyLink}
                      className="text-primary hover:opacity-80"
                    >
                      <SafeIcon icon={FiCopy} />
                    </button>
                  </div>
                </div>
                
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-primary mb-2">QR-Code</h3>
                  <div className="bg-white p-4 rounded-lg border">
                    <QRCodeSVG value={participantUrl} size={120} />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Status */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 border border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${isRoundActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className="font-medium text-primary">
                  {isRoundActive ? 'Runde aktiv' : 'Runde gestoppt'}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-primary">
                <SafeIcon icon={FiUsers} />
                <span>{poll.totalParticipants || 0} Teilnehmer</span>
              </div>
            </div>
          </div>
        </div>

        {/* Categories Grid */}
        <div className={`grid gap-6 ${poll.categories.length <= 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-3'}`}>
          {poll.categories.map((category, index) => (
            <motion.div
              key={category.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className={`bg-white rounded-lg shadow-lg p-6 border border-gray-100 ${getCategorySize(category)}`}
              style={{
                minHeight: `${Math.max(200, (category.totalInputs || 0) * 20 + 200)}px`
              }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-primary">{category.name}</h3>
                <div className="flex items-center space-x-4 text-sm text-primary">
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiTrendingUp} />
                    <span>{category.points || 0}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiMessageSquare} />
                    <span>{category.comments?.length || 0}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="bg-blue-50 rounded-lg p-3">
                  <h4 className="font-semibold text-primary mb-2">Punkte: {category.points || 0}</h4>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-500"
                      style={{ 
                        width: `${poll.categories.reduce((sum, cat) => sum + (cat.points || 0), 0) > 0 
                          ? ((category.points || 0) / poll.categories.reduce((sum, cat) => sum + (cat.points || 0), 0)) * 100 
                          : 0}%` 
                      }}
                    ></div>
                  </div>
                </div>

                {category.comments && category.comments.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    <h4 className="font-semibold text-primary">Kommentare:</h4>
                    {category.comments.map((comment, commentIndex) => (
                      <div key={commentIndex} className="bg-gray-50 rounded p-2 text-sm">
                        <p className="text-primary">{comment.text}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(comment.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HostDashboard;